import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import RichTextEditor from "react-rte";

import api from "../../../services/api";

export default function Notes({ user }) {
  const [meetingNotes, setMeetingNotes] = useState([]);

  async function fetchMeetingNotes() {
    try {
      const { data, ok } = await api.post("/meeting_note/search", { user_id: user._id, limit: 10 });
      if (!ok) return toast.error("Erreur lors du chargement des notes");
      setMeetingNotes(data);
    } catch (error) {
      toast.error("Erreur lors du chargement des notes");
    }
  }

  useEffect(() => {
    fetchMeetingNotes();
  }, [user]);

  async function handleDeleteNote(noteId) {
    if (!window.confirm("Supprimer cette note ?")) return;
    try {
      const { ok } = await api.remove(`/meeting_note/${noteId}`);
      if (!ok) return toast.error("Erreur lors de la suppression");
      toast.success("Note supprimée");
      fetchMeetingNotes();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  }

  async function handleCreateNote() {
    try {
      const { ok } = await api.post("/meeting_note", {
        user_id: user._id,
        user_name: user.name,
        user_avatar: user.avatar,
      });
      if (!ok) return toast.error("Erreur lors de la création de la note");
      toast.success("Note créée");
      fetchMeetingNotes();
    } catch (error) {
      toast.error("Erreur lors de la création de la note");
    }
  }

  return (
    <div className="mt-8 max-w-2xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={handleCreateNote}
          className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded shadow hover:from-blue-600 hover:to-blue-800 transition">
          + Create Note
        </button>
      </div>
      {meetingNotes.length === 0 && (
        <div className="text-gray-400 italic text-center py-8">
          <span className="text-2xl">🗒️</span>
          <div>No previous notes</div>
        </div>
      )}
      <ul className="space-y-4">
        {meetingNotes.map((note) => (
          <div>
            {note.created_at && (
              <div className="text-xs font-semibold text-gray-700 mb-2">
                Note of {new Date(note.created_at).toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              </div>
            )}
            <li key={note._id} className="bg-white rounded-lg shadow p-4 border border-gray-100 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="w-full">
                <NoteItem note={note} onDelete={handleDeleteNote} onUpdated={fetchMeetingNotes} />
              </div>
            </li>
          </div>
        ))}
      </ul>
    </div>
  );
}

// Subcomponent: NoteItem
function NoteItem({ note, onDelete, onUpdated }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(RichTextEditor.createValueFromString(note.text || "", "html"));

  useEffect(() => {
    setEditContent(RichTextEditor.createValueFromString(note.text || "", "html"));
  }, [note.text]);

  function handleEditChange(val) {
    setEditContent(val);
  }

  async function handleSaveEdit() {
    const html = editContent.toString("html");
    if (!html.replace(/<(.|\n)*?>/g, "").trim()) return toast.error("Note cannot be empty");
    try {
      const { ok } = await api.put(`/meeting_note/${note._id}`, {
        text: html,
      });
      if (!ok) return toast.error("Erreur lors de la modification de la note");
      toast.success("Note modifiée");
      setIsEditing(false);
      onUpdated();
    } catch (error) {
      toast.error("Erreur lors de la modification de la note");
    }
  }

  function handleCancelEdit() {
    setEditContent(RichTextEditor.createValueFromString(note.text || "", "html"));
    setIsEditing(false);
  }

  return (
    <>
      <div className="flex-1">
        {isEditing ? (
          <div>
            <RichTextEditor value={editContent} onChange={handleEditChange} className="min-h-[100px] border border-gray-200 rounded p-2 focus:outline-none" />
            <div className="flex gap-2 mt-2">
              <button onClick={handleCancelEdit} className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition">
                Cancel
              </button>
              <button onClick={handleSaveEdit} className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition">
                Save
              </button>
              <button
                onClick={() => onDelete(note._id)}
                className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition text-xs font-medium w-full"
                title="Supprimer la note">
                Delete
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="text-gray-800 text-base mb-2" dangerouslySetInnerHTML={{ __html: note.text }} />
            <div className="text-xs text-gray-400 flex items-center gap-2">
              {note.author_avatar && <img src={note.author_avatar} alt={note.author_name} className="w-5 h-5 rounded-full object-cover border border-gray-200" />}
              {note.author_name && (
                <span>
                  By <span className="font-medium text-gray-500">{note.author_name}</span>
                </span>
              )}
              {note.created_at && <span>on {new Date(note.created_at).toLocaleString()}</span>}
            </div>
          </div>
        )}
      </div>
      {!isEditing && (
        <div className="flex flex-col gap-2 md:items-end md:justify-center">
          <button onClick={() => setIsEditing(true)} className="text-blue-600 hover:underline text-xs font-medium" title="Edit note">
            Edit
          </button>
        </div>
      )}
    </>
  );
}
